import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { EmptyFilters } from "@/services/api/types";
import {
  usePeopleDistinctFilterOptions,
  usePeople,
  usePeopleInfinite,
  usePeopleJobFunctions,
  useResetPeopleInfinite
} from "@/entities/people/hooks";
import { createDataMapById } from "@/utils/datamaps";
import {
  PersonColumn,
  PersonData,
  PersonReferenceabilityLevel,
  PersonStatus,
  isReservedPersonId
} from "@/entities/people/types";
import {
  buildDisplayValueById,
  buildGroupSummaries,
  groupFieldValues,
  rekeyGroupSummaries
} from "@/components/controls/DataTable/groupSummaries";
import { GenericAggregations } from "@/services/api/types";
import { useCompanies } from "@/pages/companies/hooks";
import { StatusBadge } from "@/components/ui/status-badge";
import { getEnumTKey } from "@/utils/strings";
import { LookupNameFromIdx } from "@/utils/datamaps";
import { camelCase, chain, isEqual } from "lodash";
import { getFullName } from "@/entities/people/helpers";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { personStatusColors } from "@/components/layouts/People/PeopleTable";
import { SortingState } from "@tanstack/react-table";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { getIcpFitFilterOption } from "@/components/controls/DetailedView/ICPFit/icpFitFilter";
import { ICP_FIT_COLUMN_ID } from "@/entities/icpFit/helpers";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { OTHER_PERSON_OPTION_VALUE } from "@/pages/leads/components/CreateLeadModal";
import { getCurrentLocale } from "@/utils/locale";
import { getLocalizedCountryOptions } from "@/utils/countries";

/**
 * The stored column behind a group option the server can group by. The server
 * counts every group over the whole filtered set from these, so a group header
 * reads the same on the first page as on the last — company is grouped by id
 * and shown by name, so its summaries are re-keyed onto the name afterwards. An
 * option with no entry here groups only the rows loaded so far.
 */
const SERVER_GROUP_FIELD: Record<string, keyof PersonData> = {
  [PersonColumn.COMPANY_NAME]: "companyId",
  [PersonColumn.STATUS]: "status",
  [PersonColumn.JOB_FUNCTION]: "jobFunction"
};

interface UsePeopleTableProps {
  initialSearchTerm?: string;
  initialFilters?: typeof EmptyFilters;
  hiddenColumns?: string[];
  enabled?: boolean;
}

export const usePeopleTable = ({
  initialSearchTerm = "",
  initialFilters = EmptyFilters,
  hiddenColumns,
  enabled = true
}: UsePeopleTableProps = {}) => {
  const peopleSortByOptions = ["fullName", "companyId", "createdAt"];
  const { t } = useTranslation();
  const { enabled: showIcpFit } = useFeatureFlag(FeatureFlags.CRM_5676_ICP);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [filters, setFilters] = useState(initialFilters);
  const [groupBy, setGroupBy] = useState<string[]>([]);

  useEffect(() => {
    if (!isEqual(filters, initialFilters)) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const [sorting, onSortingChange] = useState<SortingState>([
    { id: "fullName", desc: false }
  ]);

  const sortParams = useMemo(() => {
    return {
      field: sorting[0].id ?? "fullName",
      asc: !sorting[0].desc
    };
  }, [sorting]);

  const resetPeopleInfinite = useResetPeopleInfinite();
  const isActivatingViewRef = useRef(false);

  const groupField = groupBy[0];
  const serverGroupField = groupField
    ? SERVER_GROUP_FIELD[groupField]
    : undefined;

  const {
    entities: unfilteredPeople,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    total,
    aggregations,
    isAggregationsLoading
  } = usePeopleInfinite(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    serverGroupField,
    undefined,
    sortParams.field === "fullName" ? "firstName" : sortParams.field,
    sortParams.asc,
    undefined, // fetchAll
    enabled
  );

  const people = useMemo(
    () =>
      unfilteredPeople.filter(
        (person) =>
          !isReservedPersonId(person.id) &&
          person.id !== OTHER_PERSON_OPTION_VALUE
      ),
    [unfilteredPeople]
  );

  // Removed company data fetch - using existing people data instead

  const { data: reportsTo, isSuccess: reportsToSuccess } =
    usePeopleDistinctFilterOptions("reportsTo");
  const reportsToIds = reportsTo?.values || [];

  const reportsToData = usePeople(reportsToIds, {
    enabled: reportsToSuccess && reportsToIds.length > 0
  });

  const reportsToDataMap = useMemo(
    () =>
      reportsToData.entities ? createDataMapById(reportsToData.entities) : {},
    [reportsToData.entities]
  );

  const jobFunctions = usePeopleJobFunctions().data?.jobFunctions || [];

  /** Grouped as requested — one entry per distinct value of the group field. */
  const rawAggregations = aggregations as
    | GenericAggregations<string>
    | undefined;

  const isCompanyGrouping = groupField === PersonColumn.COMPANY_NAME;

  // Grouping by company names a group per company in the whole filtered set, so
  // the names have to cover the groups the response enumerated too — a company
  // whose people are still paging in has no name anywhere else on the page.
  const groupCompanyIds = useMemo(
    () => (isCompanyGrouping ? groupFieldValues(rawAggregations) : []),
    [isCompanyGrouping, rawAggregations]
  );

  const { entities: groupCompanies } = useCompanies(groupCompanyIds, {
    enabled: groupCompanyIds.length > 0
  });

  const companyNameById = useMemo(() => {
    const names = buildDisplayValueById(people, "companyId", "companyName");
    groupCompanies.forEach((company) => {
      if (company?.id && company.name) names[company.id] = company.name;
    });
    return names;
  }, [people, groupCompanies]);

  const groupSummaries = useMemo(() => {
    if (!serverGroupField) return undefined;
    const byFieldValue = buildGroupSummaries(
      rawAggregations,
      [],
      isAggregationsLoading
    );
    // The server grouped on the company id; the column reads the name. An id
    // with no name yet keeps its own key, matching no group rather than folding
    // into the wrong one.
    return isCompanyGrouping
      ? rekeyGroupSummaries(
          byFieldValue,
          (companyId) => companyNameById[companyId] ?? companyId
        )
      : byFieldValue;
  }, [
    serverGroupField,
    rawAggregations,
    isAggregationsLoading,
    isCompanyGrouping,
    companyNameById
  ]);

  // Every group the response named is on screen from the first page, with the
  // count the server gave it — including one whose people have not been paged
  // in yet, which the loaded rows alone could not have revealed.
  const peopleGroups = useMemo(() => {
    if (!groupSummaries) return undefined;

    if (isCompanyGrouping) {
      // A key still holding an unresolved id names no group on screen yet.
      const resolvedNames = new Set(Object.values(companyNameById));
      return Object.keys(groupSummaries)
        .filter((value) => value === "" || resolvedNames.has(value))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          value,
          label: value || t("tables.notAvailable")
        }));
    }

    if (groupField === PersonColumn.STATUS) {
      // Statuses read in the order their enum declares them, and keep the badge
      // the column itself shows.
      return Object.keys(groupSummaries)
        .sort((a, b) => Number(a) - Number(b))
        .map((value) => {
          const status = Number(value) as PersonStatus;
          return {
            value,
            label: PersonStatus[status] ? (
              <StatusBadge statusColor={personStatusColors[status]}>
                {t(getEnumTKey("people", "statuses", PersonStatus, status))}
              </StatusBadge>
            ) : (
              t("tables.notAvailable")
            )
          };
        });
    }

    return Object.keys(groupSummaries)
      .map((value) => ({
        value,
        label:
          LookupNameFromIdx(
            t,
            "people.jobFunctions",
            jobFunctions,
            Number(value)
          ) || t("tables.notAvailable")
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    groupSummaries,
    groupField,
    isCompanyGrouping,
    companyNameById,
    jobFunctions,
    t
  ]);

  const countryOptions = useMemo(
    () => getLocalizedCountryOptions(getCurrentLocale()),
    []
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isError, isFetchingNextPage]);

  const resetPage = useCallback(() => {
    resetPeopleInfinite();
  }, [resetPeopleInfinite]);

  const peopleFilterOptions = useMemo<DataTableFilter[]>(() => {
    const allFilters: DataTableFilter[] = [
      {
        key: "fullName",
        filterType: { type: "text" }
      },
      {
        key: "jobTitle",
        filterType: { type: "text" }
      },
      {
        key: "email",
        filterType: { type: "text" }
      },
      {
        key: "phone",
        filterType: { type: "text" },
        customFieldLabel: t("people.phone")
      },
      {
        key: "deskPhone",
        filterType: { type: "text" }
      },
      {
        key: "address",
        filterType: { type: "text" },
        customFieldLabel: t("people.address")
      },
      {
        key: "country",
        filterType: {
          type: "map",
          values: countryOptions.map(({ code, label }) => ({
            value: code,
            label
          })),
          sort: true,
          showSearch: true
        }
      },
      {
        key: "companyId",
        filterType: {
          type: "map",
          values: chain(people)
            .filter((person) => Boolean(person.companyId))
            .groupBy("companyId")
            .map((group, companyId) => ({
              value: companyId,
              label: group[0].companyName || ""
            }))
            .value(),
          sort: true,
          showSearch: true,
          entity: EntityType.COMPANY
        }
      },
      {
        key: "status",
        excludeNoValue: true,
        filterType: {
          type: "enum",
          values: {
            enumType: PersonStatus,
            entityType: "people",
            valueType: "statuses",
            getIcon: (status: PersonStatus) => (
              <IconContainer
                size="xs"
                style={{ color: personStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      },
      {
        key: "jobFunction",
        filterType: {
          type: "map",
          values: Object.values(jobFunctions).map((jobFunction) => ({
            value: jobFunction.id,
            label: t(`people.jobFunctions.${camelCase(jobFunction.name)}`)
          })),
          sort: true
        }
      },
      {
        key: "referenceability",
        filterType: {
          type: "enum",
          values: {
            enumType: PersonReferenceabilityLevel,
            entityType: "people",
            valueType: "refLevels"
          }
        }
      },
      {
        key: "reportsTo",
        filterType: {
          type: "map",
          values: Object.values(reportsToDataMap).map((person) => ({
            value: person.id,
            label: getFullName(person)
          })),
          showAvatar: true,
          sort: true,
          showSearch: true,
          entity: EntityType.PERSON
        }
      },
      {
        key: "createdAt",
        filterType: { type: "date" }
      },
      {
        key: "statusChangedAt",
        filterType: { type: "date" }
      },
      ...(showIcpFit ? [getIcpFitFilterOption(ICP_FIT_COLUMN_ID)] : [])
    ];
    return hiddenColumns
      ? allFilters.filter((filter) => !hiddenColumns?.includes(filter.key))
      : allFilters;
  }, [
    people,
    hiddenColumns,
    jobFunctions,
    t,
    reportsToDataMap,
    showIcpFit,
    countryOptions
  ]);

  return {
    // Data
    people,
    total,
    reportsToDataMap,
    groupSummaries,
    peopleGroups,

    // Table state
    sorting,
    onSortingChange,
    isLoading,
    isError,
    refetch,
    isFetchingNextPage,
    hasNextPage,

    // Toolbar state
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    groupBy,
    setGroupBy,

    // Options
    peopleFilterOptions,
    peopleSortByOptions,

    // Actions
    handleLoadMore,
    resetPage,
    markViewActivation: useCallback(() => {
      isActivatingViewRef.current = true;
      setTimeout(() => {
        isActivatingViewRef.current = false;
      }, 0);
    }, [])
  };
};
